/**
 * Strings for Settings (pages/SettingsPage.tsx), the onboarding consent
 * step and the links to the legal pages (`legal.*`, also on onboarding).
 * Spread into the main catalog (translations.ts); `SETTINGS_EN` is typed
 * against `SETTINGS_ES`, so a missing English string fails to compile.
 */

export const SETTINGS_ES = {
  'settings.title': 'Ajustes',
  'settings.saved': 'Guardado',
  'settings.savedSynced': 'Guardado. Se aplicará también en tus otros dispositivos.',
  'settings.sections': 'Secciones de ajustes',

  // ── Account & sync ──────────────────────────────────────────────────────
  'settings.account.title': 'Cuenta y sincronización',
  'settings.account.signedOutTitle': 'Sin cuenta conectada',
  'settings.account.signedOutBody': 'Todo se guarda en este dispositivo. Conecta Google para tener una copia en tu Drive y usarla en tus otros dispositivos.',
  'settings.account.localOwner': 'Este dispositivo guarda los datos de {email}.',
  'settings.account.permissions': 'Permisos de Google',
  'settings.perm.drive': 'Google Drive',
  'settings.perm.driveHint': 'Solo los archivos que crea QUIPWA. Necesario para sincronizar.',
  'settings.perm.tasks': 'Google Tasks',
  'settings.perm.tasksHint': 'Opcional. Al terminar una sesión, crea un recordatorio en Google Tasks con la fecha del próximo repaso.',
  'settings.perm.granted': 'Concedido',
  'settings.perm.missing': 'Sin conceder',
  'settings.perm.grant': 'Conceder',
  'settings.perm.experimental': 'Experimental',
  'settings.perm.tasksWarnTitle': '¿Activar los recordatorios en Google Tasks?',
  'settings.perm.tasksWarnBody': 'Es una función experimental. Google aún no ha verificado QUIPWA para este permiso: puede mostrarte una pantalla de «aplicación no verificada», y mientras no lo esté, solo un número limitado de cuentas puede concederlo.',
  'settings.perm.tasksWarnContinue': 'Continuar a Google',

  // ── Study ───────────────────────────────────────────────────────────────
  'settings.study.title': 'Estudio',
  'settings.study.syncedNote': 'Estos valores se comparten entre todos tus dispositivos.',
  'settings.study.newPerDay': 'Tarjetas nuevas por día',
  'settings.study.newPerDayHint': 'Cada tarjeta nueva trae repasos durante semanas. Entre {min} y {max}.',
  'settings.study.maxReviews': 'Máximo de repasos por día',
  'settings.study.maxReviewsHint': 'Tope diario. Lo que lo supere pasa al día siguiente. Entre {min} y {max}.',
  'settings.study.sessionMinutes': 'Duración de una sesión',
  'settings.study.sessionMinutesHint': 'Si la cola es más larga, se divide en tramos con pausa. Entre {min} y {max} minutos.',
  'settings.study.minutesUnit': 'min',
  'settings.study.retention': 'Retención objetivo',
  'settings.study.retentionHint': 'Probabilidad de recordar una tarjeta cuando toca repasarla. Subirla un poco multiplica los repasos.',
  'settings.study.retentionLight': 'Ligera: repasas poco, olvidas más',
  'settings.study.retentionBalanced': 'Equilibrada (recomendada)',
  'settings.study.retentionIntense': 'Intensa: muchos más repasos por cada punto',
  'settings.study.reset': 'Restaurar valores por defecto',
  'settings.study.resetDone': 'Valores de estudio restaurados',

  // ── Appearance & language ───────────────────────────────────────────────
  'settings.appearance.title': 'Apariencia e idioma',
  'settings.appearance.link': 'Tema, colores y tipografía',
  'settings.appearance.linkHint': 'Abre el estudio de apariencia, con vista previa en vivo.',
  'settings.language': 'Idioma',
  'settings.languageHint': 'Idioma de la interfaz. El contenido de tus notas no se traduce.',

  // ── Shortcuts ───────────────────────────────────────────────────────────
  'settings.shortcuts': 'Atajos de teclado',
  'settings.shortcutsHint': 'Para estudiar sin ratón. Pulsa «Cambiar» y después la tecla nueva.',
  'settings.exitSession': 'Salir de la sesión',
  'settings.pressKey': 'Pulsa una tecla…',
  'settings.waiting': 'Esperando…',
  'settings.change': 'Cambiar',
  'settings.keyTaken': 'Esa tecla ya está asignada a «{action}».',
  'settings.resetShortcuts': 'Restaurar atajos por defecto',

  // ── Device data ─────────────────────────────────────────────────────────
  'settings.data.title': 'Datos de este dispositivo',
  'settings.data.storage': 'Espacio usado',
  'settings.data.storageValue': '{used} de {quota} disponibles',
  'settings.data.storageUnknown': 'Este navegador no informa del espacio usado.',
  'settings.data.persist': 'Proteger del borrado automático',
  'settings.data.persistHint': 'Pide al navegador que no borre los datos de QUIPWA cuando le falte espacio.',
  'settings.data.persisted': 'Protegido: el navegador no los borrará por falta de espacio.',
  'settings.data.persistDenied': 'El navegador no lo permitió. Instalar QUIPWA como app suele activarlo.',
  'settings.data.export': 'Descargar copia de seguridad',
  'settings.data.exportHint': 'Tu progreso de estudio, etiquetas y ajustes en un archivo SQLite. Tus mazos y notas ya están como archivos en tu Drive.',
  'settings.data.exportFailed': 'No se pudo generar la copia: {error}',
  'settings.data.wipe': 'Borrar este dispositivo',
  'settings.data.wipeHint': 'Elimina la copia local. Lo que ya está en tu Drive no se toca.',
  'settings.data.wipeConfirmTitle': '¿Borrar los datos de este dispositivo?',
  'settings.data.wipeConfirmBody': 'Se eliminará la copia local de tus mazos, notas y progreso. Lo que ya se subió a tu Drive vuelve al iniciar sesión de nuevo.',
  'settings.data.wipeConfirmPending': 'Hay {count} cambios que aún no se han subido a Drive y se perderán.',

  // ── About ───────────────────────────────────────────────────────────────
  'settings.about': 'Acerca de',
  'settings.version': 'Versión',
  'settings.engine': 'Planificador',
  'settings.storageEngine': 'Almacenamiento',
  'settings.storageEngineValue': 'SQLite en el navegador + tu Google Drive',
  'settings.license': 'Licencia',
  'settings.source': 'Código fuente',
  'settings.legal': 'Legal',
  'legal.privacy': 'Política de privacidad',
  'legal.terms': 'Términos de uso',
  'legal.nav': 'Información legal',

  // ── Onboarding: Drive permission left unticked ─────────────────────────
  'onboarding.consent.title': 'Falta el permiso de Drive',
  'onboarding.consent.body': 'Google mostró una casilla para Drive que quedó sin marcar. QUIPWA solo puede ver los archivos que crea él mismo; sin ese permiso no puede guardar nada en tu Drive.',
  'onboarding.consent.continueLocal': 'Seguir sin Drive por ahora',
  'onboarding.startLocal': 'Empezar sin cuenta',
  'onboarding.startLocalHint': 'Todo se queda en este dispositivo. Puedes conectar Google más tarde desde Ajustes.',
  'onboarding.reconnect': 'Reconectar Google',
} as const

export const SETTINGS_EN: Record<keyof typeof SETTINGS_ES, string> = {
  'settings.title': 'Settings',
  'settings.saved': 'Saved',
  'settings.savedSynced': 'Saved. It will apply on your other devices too.',
  'settings.sections': 'Settings sections',

  'settings.account.title': 'Account and sync',
  'settings.account.signedOutTitle': 'No account connected',
  'settings.account.signedOutBody': 'Everything is saved on this device. Connect Google to keep a copy in your Drive and use it on your other devices.',
  'settings.account.localOwner': 'This device holds the data of {email}.',
  'settings.account.permissions': 'Google permissions',
  'settings.perm.drive': 'Google Drive',
  'settings.perm.driveHint': 'Only the files QUIPWA creates. Needed to sync.',
  'settings.perm.tasks': 'Google Tasks',
  'settings.perm.tasksHint': 'Optional. When a session ends, adds a Google Tasks reminder with the date of the next review.',
  'settings.perm.granted': 'Granted',
  'settings.perm.missing': 'Not granted',
  'settings.perm.grant': 'Grant',
  'settings.perm.experimental': 'Experimental',
  'settings.perm.tasksWarnTitle': 'Turn on Google Tasks reminders?',
  'settings.perm.tasksWarnBody': "This is an experimental feature. Google hasn't verified QUIPWA for this permission yet: it may show you an “unverified app” screen, and until it does, only a limited number of accounts can grant it.",
  'settings.perm.tasksWarnContinue': 'Continue to Google',

  'settings.study.title': 'Study',
  'settings.study.syncedNote': 'These values are shared across all your devices.',
  'settings.study.newPerDay': 'New cards per day',
  'settings.study.newPerDayHint': 'Every new card brings reviews for weeks. Between {min} and {max}.',
  'settings.study.maxReviews': 'Max reviews per day',
  'settings.study.maxReviewsHint': 'Daily cap. Anything beyond it moves to the next day. Between {min} and {max}.',
  'settings.study.sessionMinutes': 'Session length',
  'settings.study.sessionMinutesHint': 'Longer queues are split into parts with a break. Between {min} and {max} minutes.',
  'settings.study.minutesUnit': 'min',
  'settings.study.retention': 'Target retention',
  'settings.study.retentionHint': 'How likely you are to remember a card when it comes up. Raising it a little multiplies the reviews.',
  'settings.study.retentionLight': 'Light: few reviews, more forgetting',
  'settings.study.retentionBalanced': 'Balanced (recommended)',
  'settings.study.retentionIntense': 'Intense: many more reviews per point',
  'settings.study.reset': 'Restore defaults',
  'settings.study.resetDone': 'Study values restored',

  'settings.appearance.title': 'Appearance and language',
  'settings.appearance.link': 'Theme, colours and type',
  'settings.appearance.linkHint': 'Opens the appearance studio, with a live preview.',
  'settings.language': 'Language',
  'settings.languageHint': "Interface language. Your notes' content is not translated.",

  'settings.shortcuts': 'Keyboard shortcuts',
  'settings.shortcutsHint': 'To study without a mouse. Press “Change”, then the new key.',
  'settings.exitSession': 'Exit the session',
  'settings.pressKey': 'Press a key…',
  'settings.waiting': 'Waiting…',
  'settings.change': 'Change',
  'settings.keyTaken': 'That key is already bound to “{action}”.',
  'settings.resetShortcuts': 'Restore default shortcuts',

  'settings.data.title': 'Data on this device',
  'settings.data.storage': 'Space used',
  'settings.data.storageValue': '{used} of {quota} available',
  'settings.data.storageUnknown': "This browser doesn't report the space used.",
  'settings.data.persist': 'Protect from automatic clean-up',
  'settings.data.persistHint': "Asks the browser not to delete QUIPWA's data when it runs low on space.",
  'settings.data.persisted': "Protected: the browser won't delete it to free space.",
  'settings.data.persistDenied': "The browser didn't allow it. Installing QUIPWA as an app usually does.",
  'settings.data.export': 'Download a backup',
  'settings.data.exportHint': 'Your study progress, tags and settings in a SQLite file. Your decks and notes are already files in your Drive.',
  'settings.data.exportFailed': 'Could not create the backup: {error}',
  'settings.data.wipe': 'Erase this device',
  'settings.data.wipeHint': 'Deletes the local copy. What is already in your Drive is not touched.',
  'settings.data.wipeConfirmTitle': "Erase this device's data?",
  'settings.data.wipeConfirmBody': 'The local copy of your decks, notes and progress will be deleted. What was uploaded to your Drive comes back when you sign in again.',
  'settings.data.wipeConfirmPending': "{count} changes haven't been uploaded to Drive yet and will be lost.",

  'settings.about': 'About',
  'settings.version': 'Version',
  'settings.engine': 'Scheduler',
  'settings.storageEngine': 'Storage',
  'settings.storageEngineValue': 'SQLite in the browser + your Google Drive',
  'settings.license': 'License',
  'settings.source': 'Source code',
  'settings.legal': 'Legal',
  'legal.privacy': 'Privacy policy',
  'legal.terms': 'Terms of use',
  'legal.nav': 'Legal information',

  'onboarding.consent.title': 'Drive permission missing',
  'onboarding.consent.body': "Google showed a Drive checkbox that was left unticked. QUIPWA can only see the files it creates itself; without that permission it can't save anything to your Drive.",
  'onboarding.consent.continueLocal': 'Continue without Drive for now',
  'onboarding.startLocal': 'Start without an account',
  'onboarding.startLocalHint': 'Everything stays on this device. You can connect Google later from Settings.',
  'onboarding.reconnect': 'Reconnect Google',
}
